/**
 * The parts checklist: "What do I need to pick up this week?" answered as the
 * part lines on each of the week's jobs, one tick box per line, saved on the
 * tap so the whole team sees it (docs/intelligent-chat-answers-plan.md, "Build
 * plan for the designed components", wave 3).
 *
 * ## Where the lines come from
 *
 * The ACCEPTED quote on each job, newest first — the same quote
 * `getJobQuoteSpec` (`src/lib/jobs/quote-spec.ts`) puts on the job screen, read
 * by the same rules (an unchosen option is not something to buy). A job with
 * no accepted quote has nothing to list, and that is COUNTED and said, never
 * left to read as "needs nothing" (#31: say what was looked at, never claim
 * nothing is left).
 *
 * ## Labour, and why the choice is honest rather than clever
 *
 * Quote lines carry no labour/materials column — `line_type` exists only on
 * invoice lines ("a quote cannot carry a CIS split: nothing is due yet",
 * tools.ts). So a line is classified the way the rest of the repo already
 * does it, in this order:
 *
 *   1. Linked to a price-list item → that item's own `item_type`: `material`
 *      is a part; `labour` and `service` are not (a service is a priced job
 *      like "Boiler service", not something on a merchant's shelf). The same
 *      split `lines-from-job.ts` uses for the CIS line type.
 *   2. Not linked → left out ONLY when the description plainly says it is not
 *      a thing to collect: labour (the regex `derive-duration.ts` uses for the
 *      same question), a call-out, a day or hourly rate, travel, a discount.
 *      Everything else stays on the list.
 *
 * The bias is deliberate: a stray "Gas safety certificate" line on a list to
 * tick costs a glance, a part missing from it costs a trip back to the
 * merchant. How many lines were left out is counted and shown, so the owner
 * can see the filter rather than trust it.
 *
 * ## 🚨 No money, anywhere
 *
 * Nothing here has a field for a price, and the reader never selects one. An
 * engineer holds `view_quotes: false` on purpose, and a checklist built off
 * the quote must not become the way he learns what the boss charged (#23).
 * The job-screen spec makes the same promise for the same reason.
 *
 * Pure and directive-free (anti-pattern #18): the server builds it, the panel
 * and the Mucka Bar draw it, the oversight grader summarises it.
 */

/** One line to pick up, on one job. */
export interface PartsChecklistItem {
  /** What a tick names: a `quote_line_items.id`, or a `job_parts.id` when `source` is 'part'. */
  lineId: string;
  /**
   * Where the line came from. 'part' is a part written down on the job
   * (`job_parts`, TKT-1542) — the only source for a repair or a return visit,
   * which carry no quote. Absent on blocks stored before it existed: a quote line.
   */
  source?: 'quote' | 'part';
  name: string;
  /** Null when the quote line has no quantity. Never a price. */
  quantity: number | null;
  /** Who ticked it and when, or null while it is still to get. */
  picked: PartsPickedUp | null;
}

export interface PartsPickedUp {
  /** "You", a first name, or null when the person has since left the team. */
  by: string | null;
  /** ISO timestamp. */
  at: string;
}

/** One job's lines, under a heading built on the server. */
export interface PartsChecklistJob {
  jobId: string;
  /** London YYYY-MM-DD, or null for an unscheduled job (never in a week read). */
  date: string | null;
  /** "Tue 14 Oct · Boiler swap · BS9 9ZZ". Built server-side; no customer name. */
  label: string;
  path: string;
  items: PartsChecklistItem[];
}

export interface PartsChecklistBlock {
  kind: 'parts-checklist';
  /** "Mon 12 – Sun 18 Oct". */
  rangeLabel: string;
  jobs: PartsChecklistJob[];
  /** Jobs in the range that were read at all. */
  jobsInRange: number;
  /** Of those, how many have no accepted quote — so nothing is listed for them. */
  jobsWithoutQuote: number;
  /** Lines left out as labour, a service, a call-out or a discount. */
  leftOut: number;
  /** Lines beyond what the checklist shows. Never hidden silently. */
  more: number;
  /** Where "Open this week's jobs" goes. */
  openPath: string;
  openLabel: string;
}

/** What the reader hands the builder: a job in the range. */
export interface PartsJobCandidate {
  id: string;
  /** London YYYY-MM-DD. */
  date: string | null;
  type: string | null;
  postcode: string | null;
}

/** A quote line, as read — no price columns exist on this type. */
export interface PartsLineCandidate {
  id: string;
  jobId: string;
  description: string | null;
  quantity: number | string | null;
  sortOrder: number | null;
  selectionKind: string | null;
  isDefault: boolean | null;
  /** The linked price-list item's `item_type`, or null when unlinked. */
  priceListItemType: string | null;
}

/** A part written down on a job (`job_parts`), as read — never a price. */
export interface PartsJobPartCandidate {
  id: string;
  jobId: string;
  description: string;
  quantity: number;
  /** Only collections are things to pick up; deliveries and van stock are not. */
  fulfilment: string;
  status: string;
  /** The quote line it was added from, so that line is not listed twice. */
  fromQuoteLineId: string | null;
  collectedBy: string | null;
  collectedAt: string | null;
}

/**
 * A quote line ticked off, keyed by the line. Read from the `job_parts` row the
 * tick wrote, on an account without job parts switched on; with it on, that
 * row arrives as a `PartsJobPartCandidate` instead.
 */
export interface PartsPickupCandidate {
  lineId: string;
  jobId: string;
  pickedUpBy: string | null;
  pickedUpAt: string;
}

/** Shown in the checklist; the rest are counted. */
export const PARTS_CHECKLIST_MAX_ITEMS = 40;

/**
 * Words that mean a line is not a thing to collect. `lab(ou?)r` is the
 * pattern `derive-duration.ts` already reads labour off a quote line with.
 */
const NOT_A_PART = [
  /\blab(?:ou?)r\b/i,
  /\bcall[\s-]?outs?\b/i,
  /\b(?:day|hourly|half[\s-]day)\s+rate\b/i,
  /\bper\s+(?:hour|day)\b/i,
  /\btravel(?:ling)?\b/i,
  /\bdiscount\b/i,
];

/** Is this quote line something to pick up? See the file header for the order. */
export function isPartLine(line: Pick<PartsLineCandidate, 'description' | 'priceListItemType'>): boolean {
  const type = line.priceListItemType;
  if (type === 'material') return true;
  if (type === 'labour' || type === 'service') return false;
  const text = (line.description ?? '').trim();
  return !NOT_A_PART.some(re => re.test(text));
}

/** An unchosen option is not something to buy (the same rule as quote-spec.ts). */
export function isChosen(line: Pick<PartsLineCandidate, 'selectionKind' | 'isDefault'>): boolean {
  if (!line.selectionKind || line.selectionKind === 'fixed') return true;
  return line.isDefault === true;
}

function toQuantity(v: number | string | null): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Tue 14 Oct" from a London calendar date. Spelled here, not by
 *  `toLocaleDateString`, so Node and WebKit cannot disagree about it (#41). */
export function partsDayLabel(dateIso: string): string {
  const d = new Date(`${dateIso}T12:00:00Z`);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

/** "Mon 12 – Sun 18 Oct", or "Mon 29 Sep – Sun 5 Oct" across a month end. */
export function partsRangeLabel(fromIso: string, toIso: string): string {
  const a = new Date(`${fromIso}T12:00:00Z`);
  const b = new Date(`${toIso}T12:00:00Z`);
  if (fromIso === toIso) return partsDayLabel(fromIso);
  const left = a.getUTCMonth() === b.getUTCMonth()
    ? `${WEEKDAY[a.getUTCDay()]} ${a.getUTCDate()}`
    : partsDayLabel(fromIso);
  return `${left} – ${partsDayLabel(toIso)}`;
}

function jobLabel(job: PartsJobCandidate): string {
  return [
    job.date ? partsDayLabel(job.date) : 'Not booked in',
    (job.type ?? '').trim() || 'Job',
    (job.postcode ?? '').trim() || null,
  ].filter(Boolean).join(' · ');
}

/**
 * The checklist, or null when there were no jobs in the range at all (the
 * sentence says so; an empty box with nothing to say is noise). A range with
 * jobs but nothing to pick up still gets a block, because WHY there is nothing
 * — no accepted quotes, or only labour on them — is the useful part.
 *
 * `jobs` arrive in diary order; `names` maps a `public.users.id` to how a
 * ticker is shown ("You" for the viewer, a first name otherwise).
 */
export function partsChecklistBlock(input: {
  jobs: readonly PartsJobCandidate[];
  /** Job ids that have an accepted quote, whether or not it has part lines. */
  quotedJobIds: ReadonlySet<string>;
  lines: readonly PartsLineCandidate[];
  pickups: readonly PartsPickupCandidate[];
  /** Parts written down on these jobs (TKT-1542). Listed before the quote's lines. */
  jobParts?: readonly PartsJobPartCandidate[];
  names: ReadonlyMap<string, string>;
  rangeLabel: string;
  openPath: string;
  openLabel: string;
}): PartsChecklistBlock | null {
  if (!input.jobs.length) return null;

  const picked = new Map<string, PartsPickupCandidate>();
  for (const p of input.pickups) picked.set(`${p.jobId}:${p.lineId}`, p);

  // Parts written down on the job: collections still to fetch, or fetched
  // (so the tick shows). A delivery, van stock or a fitted part is nothing to
  // pick up. A quote line already added to the list is listed once, as a part.
  const partsByJob = new Map<string, PartsChecklistItem[]>();
  const addedLines = new Set<string>();
  for (const p of input.jobParts ?? []) {
    if (p.fromQuoteLineId) addedLines.add(p.fromQuoteLineId);
    if (p.fulfilment !== 'collect' || p.status === 'fitted') continue;
    const item: PartsChecklistItem = {
      lineId: p.id,
      source: 'part',
      name: p.description,
      quantity: p.quantity,
      picked: p.status === 'collected'
        ? { by: p.collectedBy ? input.names.get(p.collectedBy) ?? null : null, at: p.collectedAt ?? '' }
        : null,
    };
    const list = partsByJob.get(p.jobId);
    if (list) list.push(item);
    else partsByJob.set(p.jobId, [item]);
  }

  const byJob = new Map<string, PartsLineCandidate[]>();
  let leftOut = 0;
  for (const line of input.lines) {
    if (addedLines.has(line.id)) continue;
    if (!isChosen(line)) continue;
    const name = (line.description ?? '').trim();
    if (!name) continue;
    const qty = toQuantity(line.quantity);
    // A zero or negative quantity is a credit or a placeholder, not a thing to fetch.
    if (qty != null && qty <= 0) { leftOut++; continue; }
    if (!isPartLine(line)) { leftOut++; continue; }
    const list = byJob.get(line.jobId);
    if (list) list.push(line);
    else byJob.set(line.jobId, [line]);
  }

  const jobs: PartsChecklistJob[] = [];
  let shown = 0;
  let more = 0;
  for (const job of input.jobs) {
    const lines = (byJob.get(job.id) ?? [])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const all: PartsChecklistItem[] = [
      ...(partsByJob.get(job.id) ?? []),
      ...lines.map((l): PartsChecklistItem => {
        const p = picked.get(`${job.id}:${l.id}`);
        return {
          lineId: l.id,
          name: (l.description ?? '').trim(),
          quantity: toQuantity(l.quantity),
          picked: p ? { by: p.pickedUpBy ? input.names.get(p.pickedUpBy) ?? null : null, at: p.pickedUpAt } : null,
        };
      }),
    ];
    if (!all.length) continue;
    const room = Math.max(0, PARTS_CHECKLIST_MAX_ITEMS - shown);
    more += Math.max(0, all.length - room);
    if (!room) continue;
    const items = all.slice(0, room);
    shown += items.length;
    jobs.push({ jobId: job.id, date: job.date, label: jobLabel(job), path: `/jobs/${job.id}`, items });
  }

  return {
    kind: 'parts-checklist',
    rangeLabel: input.rangeLabel,
    jobs,
    jobsInRange: input.jobs.length,
    // "Nothing written down": no accepted quote AND no parts on the job.
    jobsWithoutQuote: input.jobs.filter(j => !input.quotedJobIds.has(j.id) && !partsByJob.has(j.id)).length,
    leftOut,
    more,
    openPath: input.openPath,
    openLabel: input.openLabel,
  };
}

/** Every item on the block, in order. */
export function partsChecklistItems(block: Pick<PartsChecklistBlock, 'jobs'>): PartsChecklistItem[] {
  return block.jobs.flatMap(j => j.items);
}

/** "×3", or nothing when there is no quantity or it is one. */
export function partsQuantityLabel(q: number | null): string | null {
  if (q == null || q === 1) return null;
  return `× ${Number.isInteger(q) ? q : q.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
}

/** "3 left to pick up", "1 left to pick up", "Everything's picked up". */
export function partsLeftLabel(left: number): string {
  if (left <= 0) return "Everything's picked up";
  return `${left} left to pick up`;
}

/** Why there is nothing to tick, said plainly. Null when there is something. */
export function partsEmptyLabel(block: PartsChecklistBlock): string | null {
  if (block.jobs.length) return null;
  const n = block.jobsInRange;
  const jobs = `${n} job${n === 1 ? '' : 's'}`;
  if (block.jobsWithoutQuote === n) {
    return `None of the ${jobs} has parts written down or an accepted quote, so there is nothing to list. That doesn't mean they need nothing.`;
  }
  return `Nothing to pick up on the accepted quotes for these ${jobs}${block.leftOut ? ', only labour and services' : ''}.`;
}

/**
 * The small print under the list: where the lines came from and what was
 * left out, so the filter is visible rather than trusted. Null when there is
 * nothing to say.
 */
export function partsFootnote(block: PartsChecklistBlock): string | null {
  const bits: string[] = [];
  if (block.more) bits.push(`${block.more} more not shown`);
  if (block.jobs.length && block.jobsWithoutQuote) {
    bits.push(`${block.jobsWithoutQuote} job${block.jobsWithoutQuote === 1 ? ' has' : 's have'} no parts written down and no accepted quote, so nothing is listed for ${block.jobsWithoutQuote === 1 ? 'it' : 'them'}`);
  }
  if (block.leftOut) bits.push(`${block.leftOut} labour or service line${block.leftOut === 1 ? '' : 's'} left out`);
  return bits.length ? `${bits.join('. ')}.` : null;
}

/**
 * The tool RESULT: the same list in words, because the reply's sentence must
 * carry the answer on its own (phone, WhatsApp and the grader see text) and
 * the model cannot see the checklist. No prices, because the block has none.
 *
 * 🚨 Says what was looked at — the accepted quote on each job — and never that
 * nothing is left to look at (#31). A job with no accepted quote is named as
 * exactly that, not as a job that needs nothing.
 */
export function partsChecklistResultText(
  block: PartsChecklistBlock,
  opts: { onScreen: boolean },
): string {
  const n = block.jobsInRange;
  const jobs = `${n} job${n === 1 ? '' : 's'}`;
  const out: string[] = [];
  const items = partsChecklistItems(block);
  if (!items.length) {
    out.push(`Looked at the parts written down and the accepted quote on each of the ${jobs} booked in ${block.rangeLabel}. ${partsEmptyLabel(block)}`);
    if (block.jobsWithoutQuote) out.push('If they ask what to pick up, ask what they are fitting rather than saying nothing is needed.');
    return out.join(' ');
  }
  out.push(`Parts written down on, and on the accepted quotes for, the ${jobs} booked in ${block.rangeLabel} (no prices; labour and services left out; deliveries and van stock are not on it):`);
  for (const job of block.jobs) {
    const lines = job.items.map(i => {
      const qty = i.quantity != null ? ` x${Number.isInteger(i.quantity) ? i.quantity : i.quantity.toFixed(2)}` : '';
      const got = i.picked ? ` (picked up${i.picked.by ? ` by ${i.picked.by}` : ''})` : '';
      return `${i.name}${qty}${got}`;
    });
    out.push(`- ${job.label}: ${lines.join('; ')}`);
  }
  const left = items.filter(i => !i.picked).length;
  out.push(`${left} of ${items.length} still to pick up.`);
  const foot = partsFootnote(block);
  if (foot) out.push(foot);
  if (block.jobsWithoutQuote) out.push('A job with nothing written down is not a job that needs nothing; say so if it matters. A part they mention can be written down with job_parts.');
  if (opts.onScreen) {
    out.push('This list is on screen as a checklist that saves for the whole team as they tick. '
      + 'Do not read every line out: say how many are left to pick up and name anything big.');
  }
  return out.join('\n');
}

/**
 * One line for the oversight grader: counts and states only, nothing a
 * customer could be identified by, and no figure that is money.
 */
export function summarisePartsChecklistBlock(block: PartsChecklistBlock): string {
  const items = partsChecklistItems(block);
  const total = items.length + block.more;
  const picked = items.filter(i => i.picked).length;
  const parts = [
    `${total} part${total === 1 ? '' : 's'} across ${block.jobs.length} job${block.jobs.length === 1 ? '' : 's'}`,
    `${picked} picked up`,
    block.jobsWithoutQuote ? `${block.jobsWithoutQuote} of ${block.jobsInRange} jobs with nothing written down` : null,
    block.leftOut ? `${block.leftOut} labour/service lines left out` : null,
    block.more ? `${block.more} not shown` : null,
    'tick saves for the team',
  ].filter(Boolean);
  return `[parts-checklist: ${parts.join(', ')}]`;
}
